import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Icon from '@atlaskit/icon';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import { JiraIcon } from '@atlaskit/logo';
import { LinkItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { LaunchDarklyGlyph } from '../../../common/assets/launch-darkly-glyph.tsx';
import { SlackGlyph } from '../../../common/assets/slack-glyph.tsx';
import type { ProjectProps } from '../../../common/types.tsx';
import {
	buildDiscoverAutomationUrl,
	buildTemplateRuleAutomationUrl,
} from '../../../common/utils/automation-urls.tsx';
import messages from './messages.tsx';

const jiraIssuesIcon = <IssuesIcon LEGACY_size="medium" spacing="spacious" label="" />;
const slackIcon = <Icon size="medium" glyph={SlackGlyph} label="" />;
const launchDarklyGliph = <Icon size="medium" glyph={LaunchDarklyGlyph} label="" />;

const ActionAndTriggerIcons = ({ trigger }: { trigger: ReactNode }) => (
	<>
		<IconShadow>
			<JiraIcon
				size="small"
				appearance="brand"
				testId="software-releases-automation-menu.ui.menu-contents.template-rules.jira-icon"
				label=""
			/>
		</IconShadow>
		<ArrowRightIcon spacing="spacious" label="" />
		<IconShadow>{trigger}</IconShadow>
	</>
);

export const AutomationMenuTemplateRules = (props: ProjectProps) => {
	const { isSimplifiedProject, projectKey, projectType } = props;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});
	return (
		<>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_14',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={jiraIssuesIcon} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateRuleAddVersionToInProgressIssue')}
			>
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.templateRuleAddIssueToVersionOnVersionCreateIssueTerminologyRefreshM4
						: messages.templateRuleAddIssueToVersionOnVersionCreate,
				)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'devops_template_6',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={launchDarklyGliph} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateRuleCreateLaunchDarklyFlag')}
			>
				{formatMessage(messages.templateRuleCreateLaunchDarklyFlagOnVersionCreate)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_12',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={slackIcon} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateSendSlackMessageOnVersionRelease')}
			>
				{formatMessage(messages.templateRuleSendSlackMessageOnVersionRelease)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_13',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={slackIcon} />}
				onClick={() =>
					fireUIAnalytics(analyticsEvent, 'templateSendSlackMessageOnVersionUnRelease')
				}
			>
				{formatMessage(messages.templateRuleSendSlackMessageOnVersionUnRelease)}
			</LinkItem>
			<DiscoverAutomationLink
				href={buildDiscoverAutomationUrl({
					projectKey,
					projectType,
					isSimplified: isSimplifiedProject,
				})}
				onClick={() => fireUIAnalytics(analyticsEvent, 'discoverAutomationLink')}
			>
				{formatMessage(messages.discoverMoreAutomationMenuItemLabel)}
			</DiscoverAutomationLink>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconShadow = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${4 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${4 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${0.5 * gridSize}px`,
	boxShadow: `${token('elevation.shadow.raised')}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DiscoverAutomationLink = styled.a({
	paddingTop: token('space.200'),
	paddingRight: '0px',
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
});
